import { Check, CircleDashed, Clock, MinusCircle, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { StepDecision, StepStatus, SubmissionStep } from '../types'
import { AvatarStack, type StackPerson } from './AvatarStack'
import { DecisionBadge } from './badges'

/** The subset of a step the timeline needs (a resolved step or a routing preview). */
export interface TimelineStep {
    step: number
    name: string
    mode: 'any' | 'all'
    status: StepStatus
    assignees: { id: string; name: string; email: string; via: string }[]
}

const nodeStyle: Record<StepStatus, string> = {
    approved: 'bg-primary text-primary-foreground border-primary',
    active: 'bg-background text-primary border-primary ring-4 ring-primary/15',
    awaiting_requester: 'bg-background text-amber-700 border-amber-500 ring-4 ring-amber-500/15 dark:text-amber-400',
    refused: 'bg-destructive text-white border-destructive',
    pending: 'bg-background text-muted-foreground border-border',
    skipped: 'bg-muted text-muted-foreground border-border',
}

const stateLabel: Record<StepStatus, string> = {
    approved: 'Approved',
    active: 'In review',
    awaiting_requester: 'Awaiting requester',
    refused: 'Refused',
    pending: 'Waiting',
    skipped: 'Skipped',
}

function StepIcon({ status, n }: { status: StepStatus; n: number }) {
    switch (status) {
        case 'approved': return <Check className="size-4" />
        case 'refused': return <X className="size-4" />
        case 'active':
        case 'awaiting_requester': return <Clock className="size-4" />
        case 'skipped': return <MinusCircle className="size-4" />
        default: return <span className="text-xs font-semibold">{n}</span>
    }
}

interface Props {
    steps: (SubmissionStep | TimelineStep)[]
    decisions?: StepDecision[]
    className?: string
    /** "horizontal" collapses to vertical on small screens automatically. */
    orientation?: 'auto' | 'vertical'
}

/**
 * Stepper for an approval workflow: state node, avatar stack of the resolved
 * assignees (with per-person decided/refused rings for "all must approve"
 * steps), the recorded decisions and their comments.
 */
export function WorkflowTimeline({ steps, decisions = [], className, orientation = 'auto' }: Props) {
    if (steps.length === 0) {
        return (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <CircleDashed className="size-4" /> This submission has no approval workflow.
            </p>
        )
    }
    return (
        <ol className={cn('flex flex-col gap-0', orientation === 'auto' && 'lg:flex-row lg:gap-3', className)}>
            {steps.map((s, i) => {
                const round = 'round' in s ? s.round : 1
                const stepDecisions = decisions.filter((d) => d.step === s.step && d.round === round)
                const people: StackPerson[] = s.assignees.map((a) => {
                    const d = stepDecisions.find((x) => x.decided_by === a.id)
                    return {
                        id: a.id, name: a.name, email: a.email, via: a.via,
                        state: d ? (d.decision === 'refuse' ? 'refused' : 'done') : s.status === 'active' || s.status === 'awaiting_requester' ? 'waiting' : undefined,
                    }
                })
                const last = i === steps.length - 1
                return (
                    <li key={s.step} className={cn('relative flex gap-3 pb-6 last:pb-0', orientation === 'auto' && 'lg:flex-1 lg:flex-col lg:pb-0')}>
                        {/* connector */}
                        {!last && (
                            <span
                                aria-hidden
                                className={cn(
                                    'absolute left-4 top-9 -ml-px h-[calc(100%-2.25rem)] w-0.5',
                                    s.status === 'approved' ? 'bg-primary' : 'bg-border',
                                    orientation === 'auto' && 'lg:left-9 lg:top-4 lg:ml-0 lg:h-0.5 lg:w-[calc(100%-2.25rem)]',
                                )}
                            />
                        )}
                        <span className={cn('relative z-10 flex size-8 shrink-0 items-center justify-center rounded-full border-2', nodeStyle[s.status])}>
                            <StepIcon status={s.status} n={i + 1} />
                        </span>
                        <div className="flex min-w-0 flex-1 flex-col gap-1.5 lg:pr-3">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-semibold text-foreground">{s.name || `Step ${i + 1}`}</span>
                                <Badge variant={s.status === 'active' ? 'default' : 'outline'} className="text-[10px]">
                                    {stateLabel[s.status]}
                                </Badge>
                                {s.mode === 'all' && s.assignees.length > 1 && (
                                    <span className="text-xs text-muted-foreground">
                                        {stepDecisions.filter((d) => d.decision !== 'refuse').length}/{s.assignees.length} approved
                                    </span>
                                )}
                            </div>
                            <AvatarStack people={people} />
                            {stepDecisions.map((d) => (
                                <div key={d.decided_by + d.decided_at} className="flex flex-col gap-0.5 rounded-md bg-muted/50 px-3 py-2 text-xs">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-medium text-foreground">{d.decided_by_name}</span>
                                        <DecisionBadge decision={d.decision} />
                                        <span className="text-muted-foreground">{new Date(d.decided_at).toLocaleString()}</span>
                                    </div>
                                    {d.comment && <p className="text-muted-foreground">“{d.comment}”</p>}
                                </div>
                            ))}
                        </div>
                    </li>
                )
            })}
        </ol>
    )
}
